import { alpha } from '@mui/material/styles';
import VideoLinkTag from '../components/common/VideoLinkTag';
import SuggestedActivityCard from '../components/chat/SuggestedActivityCard';
import SafetyWarning from '../components/chat/SafetyWarning';

export const enhancementsConfig = {
  /**
   * Configure which resource types should be automatically scanned and linked in text.
   */
  resourceAutoLinking: {
    resourceTypes: [
      'exercise_definition',
      'equipment',
      'workout_plan',
      'workout_session',
      'location'
    ],
  },
  
  /**
   * Components to inject at the bottom of a message bubble when a specific 
   * regex pattern is found in the text. The pattern is stripped from the text.
   */
  messageAppendages: [
    {
      pattern: /\[SAFETY\/\]/gi,
      component: SafetyWarning
    }
  ],

  /**
   * Custom tags to style text within chat messages.
   * Format: [TAG]content[/TAG]
   */
  customTags: {
    'V': {
      component: VideoLinkTag,
    },
    'PREVIOUS': {
      component: 'span',
      sx: {
        display: 'inline-block',
        backgroundColor: (theme) => alpha(theme.palette.text.secondary, 0.08),
        color: 'text.primary',
        border: '1px solid',
        borderColor: (theme) => theme.palette.divider,
        borderRadius: '4px',
        padding: '0px 6px',
        fontSize: '0.9em',
        fontWeight: 500,
        mx: 0.25,
        verticalAlign: 'baseline',
      },
      label: 'Previous Performance'
    },
    'SUGGESTED': {
      component: SuggestedActivityCard,
      label: 'Suggestion'
    }
  },

  /**
   * 'definitions' is the first type of enhancement. When a key from this object
   * is found in a chat message, it will be made clickable to show a modal
   * with its term and definition.
   */
  definitions: {
    'Hypertrophy': {
      term: 'Hypertrophy',
      definition: 'Hypertrophy is the increasing the size of your muscle fibers—aka building bigger muscles. It’s different from pure strength training, which focuses more on lifting the heaviest weight possible, and different from endurance, which focuses on long efforts.',
    },
    'eccentric': {
      term: 'Eccentric',
      definition: 'The phase of a movement where a muscle lengthens while under tension, most commonly during the lowering portion of an exercise. For example, when lowering a dumbbell in a biceps curl or descending in a squat.',
      caseSensitive: true,
    },
    'concentric': {
      term: 'Concentric',
      definition: 'The phase of a movement where the muscle shortens while contracting against resistance. This is typically the "lifting" or "pushing" part of an exercise (e.g., the upward motion of a squat or the lifting phase of a bicep curl).',
      caseSensitive: false,
    },
    'isometric': {
      term: 'Isometric',
      definition: 'A type of muscle contraction where the muscle length does not change during the exercise. Examples include holding a Plank or pausing at the bottom of a squat.',
      caseSensitive: false,
    },
    'DOMS': {
      term: 'Delayed Onset Muscle Soreness',
      definition: 'The pain and stiffness felt in muscles several hours to days after unaccustomed or strenuous exercise. It is a normal response to unusual exertion and is part of an adaptation process that leads to greater strength and stamina.',
      caseSensitive: true,
    },
    'RPE': {
      term: 'Rate of Perceived Exertion',
      definition: 'RPE is a scale from 0-10 used to measure the intensity of your exercise, based on how hard you feel you are working.\n\n0: No exertion at all\n1-2: Very light activity\n3: Light activity\n4-5: Moderate activity\n6-7: Vigorous activity\n8-9: Very hard activity\n10: Maximal effort',
      caseSensitive: true,
    },
    'RIR': {
      term: 'Reps In Reserve',
      definition: 'How many more reps you could have done at the end of a set before failure. For example, 2 RIR means you stopped the set but could have physically performed 2 more reps.',
      caseSensitive: true,
    },
    'RM': {
      term: 'Rep Max',
      definition: 'The most weight you can lift for a given number of reps with good form.',
      caseSensitive: true,
    },
    '1RM': {
      term: 'One Rep Max',
      definition: 'The maximum amount of weight you can lift for a single repetition of an exercise with proper form.',
      caseSensitive: true,
    },
    'reps': {
      term: 'Repetitions',
      definition: 'Repetitions refers to the number of times you perform a single complete movement of an exercise before taking a break or rest.',
      caseSensitive: true,
    },
    'volume': {
      term: 'Training Volume',
      definition: 'The total amount of work performed in a training session or week. It is typically calculated as: Sets × Reps × Weight.',
      caseSensitive: false,
    },
    'PR': {
      term: 'Personal Record',
      definition: 'The best performance you have achieved on a specific exercise, usually referring to the maximum weight lifted.',
      caseSensitive: true,
    },
    'progressive overload': {
      term: 'Progressive Overload',
      definition: 'The fundamental principle of fitness that involves gradually increasing the stress placed on the body during exercise training (by increasing weight, reps, or frequency) over time to continuously stimulate muscle growth and strength gains.',
      caseSensitive: false,
    },
    'interval': {
      term: 'Intervals',
      definition: 'Alternate a specific bout of higher-intensity exercise with a specific bout of lower-intensity exercise or rest. This can be based on time (for example, 30 seconds hard, 60 seconds easy) or distance (for example, 400 m fast, 200 m easy).',
      caseSensitive: true,
    },
    'intervals': {
      term: 'Intervals',
      definition: 'Alternate a specific bout of higher-intensity exercise with a specific bout of lower-intensity exercise or rest. This can be based on time (for example, 30 seconds hard, 60 seconds easy) or distance (for example, 400 m fast, 200 m easy).',
      caseSensitive: true,
    },
    'HIIT': {
      term: 'High-Intensity Interval Training',
      definition: 'A form of interval training, a cardiovascular exercise strategy alternating short periods of intense anaerobic exercise with less intense recovery periods.',
      caseSensitive: true,
    },
    'superset': {
      term: 'Superset',
      definition: 'Performing two exercises back-to-back with no rest in between. This is often done to save time or to increase the intensity of a workout.',
      caseSensitive: false,
    },
    'dropset': {
      term: 'Drop Set',
      definition: 'A technique where you perform an exercise until failure, then immediately lower the weight and continue for more reps until failure again.',
      caseSensitive: false,
    },
    'compound': {
      term: 'Compound Exercise',
      definition: 'An exercise that involves movement at more than one joint and recruits multiple muscle groups simultaneously (e.g., Squats, Deadlifts, Bench Press).',
      caseSensitive: false,
    },
    'isolation': {
      term: 'Isolation Exercise',
      definition: 'An exercise that involves movement at only one joint and targets a specific muscle group (e.g., Bicep Curls, Leg Extensions).',
      caseSensitive: false,
    },
    'AMRAP': {
      term: 'As Many Reps As Possible',
      definition: 'A set structure where you perform a specific exercise for as many repetitions as you can with good form, rather than aiming for a fixed number.',
      caseSensitive: true,
    },
    'deload': {
      term: 'Deload',
      definition: 'A planned period of recovery (usually a week) where training volume or intensity is reduced to allow the body to recover from accumulated fatigue and prevent overtraining.',
      caseSensitive: false,
    },
    'ROM': {
      term: 'Range of Motion',
      definition: 'The measurement of movement around a specific joint or body part. "Full ROM" means moving the weight through the complete extent of the movement.',
      caseSensitive: true,
    },
    'Epley': {
      term: 'The Epley Formula',
      definition: 'The Epley formula is a mathematical equation commonly used to estimate your one-rep max (1RM). Formula: 1RM = Weight lifted × (1 + 30/Reps)',
      caseSensitive: true,
    },
    'DB': {
      term: 'Dumbbell',
      definition: 'DB is short for the term \'dumbbell\'.',
      caseSensitive: true,
    },
    'BB': {
      term: 'Barbell',
      definition: 'BB is short for "Barbell," a long metal bar to which discs of varying weights are attached at each end.',
      caseSensitive: true,
    },
    'KB': {
      term: 'Kettlebell',
      definition: 'KB is short for "Kettlebell," a cast-iron or cast-steel ball with a handle attached to the top.',
      caseSensitive: true,
    },
  },
};